import React, { useState, useEffect, useRef } from 'react';
import VehiclesManagementHeader from './VehiclesManagementHeader';
import VehiclesTable from './VehiclesTable';
import { Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import EditVehicleDialog from './editvehicles/EditVehicleDialog';
import AddVehicleDialog from './addvehicles/AddVehicleDialog';
import DeleteVehicleDialog from './deletevehicles/DeleteVehicleDialog';
import vehicleService from '@/services/vehicleService';
import masterDataService from '@/services/masterDataService';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import { usePagination } from '@/hooks/usePagination'; 
import { CommonSearchInput } from '@/components/ui/common-search-input';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const VehiclesManagementPage = () => {
  const { userProfile } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [editVehicleDialogOpen, setEditVehicleDialogOpen] = useState(false);
  const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
  const [deleteVehicleDialogOpen, setDeleteVehicleDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);
  const [vehicleDetailsLoading, setVehicleDetailsLoading] = useState(false);
  const [totalVehicles, setTotalVehicles] = useState(0); 
  
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });
  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setSearchQuery(search);
    fetchVehicles(search);
  }, [currentPage, pageSize, searchParams.get('search')]);

  
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentSearchParam = searchParams.get('search') || '';
      if (searchQuery !== currentSearchParam) {
        handleSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchVehicles = async (query = searchQuery) => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize; 
      const response = await vehicleService.getVehicles(skip, pageSize, false, false, query);
      
      let vehiclesData, totalCount;
      
      if (response && typeof response === 'object' && Array.isArray(response.data)) {
        vehiclesData = response.data;
        totalCount = response.total || response.count || vehiclesData.length;
      } 
      else if (Array.isArray(response)) {
        vehiclesData = response;
        totalCount = response.length < pageSize ? skip + response.length : skip + response.length + 1;
      } 
      else if (response && typeof response === 'object' && Array.isArray(response.vehicles)) {
        vehiclesData = response.vehicles;
        totalCount = response.total || response.count || vehiclesData.length;
      } 
      else {
        vehiclesData = [];
        totalCount = 0;
      }
      
      const transporterData = await masterDataService.getTransporters(0, 500, false);
      
      const transporterMap = {};
      transporterData.forEach(transporter => {
        transporterMap[transporter.id] = transporter.company_name || transporter.id;
      });
      
      const transformedVehicles = vehiclesData.map(vehicle => ({
        id: vehicle.id,
        vehicleNumber: vehicle.vehicle_number,
        vehicleType: vehicle.vehicle_type,
        capacity: vehicle.capacity,
        transporterId: vehicle.transporter_id,
        transporterName: transporterMap[vehicle.transporter_id] || vehicle.transporter_id,
        status: vehicle.is_active ? 'Active' : 'Inactive',
        createdAt: vehicle.created_at ? new Date(vehicle.created_at).toLocaleDateString() : 'N/A'
      }));
      
      setVehicles(transformedVehicles);
      setTotalVehicles(totalCount); 
      setError(null);
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
      setError('Failed to load vehicles');
      toast({
        title: "Error",
        description: "Failed to load vehicles. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const fetchVehicleById = async (vehicleId) => {
    try {
      setVehicleDetailsLoading(true);
      const vehicleData = await vehicleService.getVehicleById(vehicleId);
      const transporterData = await masterDataService.getTransporters(0, 500, false);
      
      const transporterMap = {};
      transporterData.forEach(transporter => {
        transporterMap[transporter.id] = transporter.company_name || transporter.id;
      });
      
      const transformedVehicle = {
        id: vehicleData.id,
        vehicleNumber: vehicleData.vehicle_number,
        vehicleType: vehicleData.vehicle_type,
        capacity: vehicleData.capacity,
        transporterId: vehicleData.transporter_id,
        transporterName: transporterMap[vehicleData.transporter_id] || vehicleData.transporter_id,
        status: vehicleData.is_active ? 'Active' : 'Inactive',
        createdAt: vehicleData.created_at ? new Date(vehicleData.created_at).toLocaleDateString() : 'N/A'
      };
      setSelectedVehicleDetails(transformedVehicle);
      return transformedVehicle;
    } catch (err) {
      console.error('Failed to fetch vehicle details:', err);
      toast({
        title: "Error",
        description: "Failed to load vehicle details. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setVehicleDetailsLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setAddVehicleDialogOpen(true);
  };

  const handleEditVehicle = async (vehicleId) => {
    const vehicleDetails = await fetchVehicleById(vehicleId);
    if (vehicleDetails) {
      setSelectedVehicleDetails(vehicleDetails);
      setEditVehicleDialogOpen(true);
    }
  };

  const handleDeleteVehicle = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    setDeleteVehicleDialogOpen(true);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const newParams = new URLSearchParams(searchParams);
    
    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }
    
    newParams.set('page', '1');
    setPage(1);
    
    setSearchParams(newParams);
  };

  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);
  };

  const handleCreateVehicle = async (newVehicle) => {
    try {
      const createdVehicle = await vehicleService.createVehicle(newVehicle);
      
      const transporterData = await masterDataService.getTransporters(0, 500, false);
      const transporterMap = {};
      transporterData.forEach(transporter => {
        transporterMap[transporter.id] = transporter.company_name || transporter.id;
      });
      
      const transformedVehicle = {
        id: createdVehicle.id,
        vehicleNumber: createdVehicle.vehicle_number,
        vehicleType: createdVehicle.vehicle_type,
        capacity: createdVehicle.capacity,
        transporterId: createdVehicle.transporter_id,
        transporterName: transporterMap[createdVehicle.transporter_id] || createdVehicle.transporter_id,
        status: createdVehicle.is_active ? 'Active' : 'Inactive',
        createdAt: createdVehicle.created_at ? new Date(createdVehicle.created_at).toLocaleDateString() : 'N/A'
      };
      
      fetchVehicles();
      setAddVehicleDialogOpen(false);
      toast({
        title: "Vehicle Added",
        description: `${newVehicle.vehicle_number} has been successfully added.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add vehicle. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateVehicle = async (updatedVehicle) => {
    try {
      const vehicleData = {
        vehicle_number: updatedVehicle.vehicle_number,
        vehicle_type: updatedVehicle.vehicle_type,
        capacity: updatedVehicle.capacity,
        transporter_id: updatedVehicle.transporter_id,
        is_active: updatedVehicle.is_active
      };
      
      const response = await vehicleService.updateVehicle(updatedVehicle.id, vehicleData);

      const transporterData = await masterDataService.getTransporters(0, 500, false);
      const transporterMap = {};
      transporterData.forEach(transporter => {
        transporterMap[transporter.id] = transporter.company_name || transporter.id;
      });
      
      const transformedVehicle = {
        id: response.id,
        vehicleNumber: response.vehicle_number,
        vehicleType: response.vehicle_type,
        capacity: response.capacity,
        transporterId: response.transporter_id,
        transporterName: transporterMap[response.transporter_id] || response.transporter_id,
        status: response.is_active ? 'Active' : 'Inactive',
        createdAt: response.created_at ? new Date(response.created_at).toLocaleDateString() : 'N/A'
      };

      fetchVehicles();
      setEditVehicleDialogOpen(false);
      setSelectedVehicleDetails(null);
      toast({
        title: "Vehicle Updated",
        description: `${transformedVehicle.vehicleNumber} has been successfully updated.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update vehicle. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeleteVehicle = async (vehicleToDelete) => {
    try {
      await vehicleService.deleteVehicle(vehicleToDelete.id);

      fetchVehicles();
      setDeleteVehicleDialogOpen(false);
      setSelectedVehicle(null);
      toast({
        title: "Vehicle Deleted",
        description: `${vehicleToDelete.vehicleNumber} has been successfully deleted.`,
      });
    } catch (error) {
      console.error('Delete vehicle error:', error);
      toast({
        title: "Error",
        description: `Failed to delete vehicle: ${error.message || 'Please try again.'}`,
        variant: "destructive",
      });
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
  }

  const totalPages = Math.ceil(totalVehicles / pageSize); 

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Vehicles Management</h1>
          <p className="text-xs text-muted-foreground">Register and audit active trucks and transport fleet units.</p>
        </div>
        <div className="flex items-center space-x-2">
          <CommonButton onClick={handleAddVehicle} variant="default" icon={Plus}>
            Add Vehicle
          </CommonButton>
        </div>
      </div>
      
      <VehiclesManagementHeader vehicles={vehicles} />
      
      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by vehicle number..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            maxLength={15}
          />
        </div>
      </div>
      
      <TableBlurLoader 
        isLoading={loading} 
        className="mt-4"
      >
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)'}}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {vehicles.length > 0 ? (
              <VehiclesTable 
                vehicles={vehicles} 
                onEditVehicle={handleEditVehicle}
                onDeleteVehicle={handleDeleteVehicle}
                currentPage={currentPage} 
                totalPages={totalPages} 
                totalVehicles={totalVehicles} 
                pageSize={pageSize} 
                onPageChange={setPage} 
                userRole={userProfile?.role}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? "" : "No data available"}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <EditVehicleDialog
        open={editVehicleDialogOpen}
        onOpenChange={(open) => {
          setEditVehicleDialogOpen(open);
          if (!open) setSelectedVehicleDetails(null);
        }}
        vehicle={selectedVehicleDetails}
        onUpdateVehicle={handleUpdateVehicle}
      />
      
      <AddVehicleDialog
        open={addVehicleDialogOpen}
        onOpenChange={setAddVehicleDialogOpen}
        onAddVehicle={handleCreateVehicle}
      />
 
      
      {selectedVehicle && (
        <DeleteVehicleDialog
          open={deleteVehicleDialogOpen}
          onOpenChange={setDeleteVehicleDialogOpen}
          vehicle={selectedVehicle}
          onDeleteVehicle={handleConfirmDeleteVehicle}
        />
      )}
    </div>
  );
};

export default VehiclesManagementPage;